import { Modal } from "@mui/material";
import React, { useState, useEffect, useRef, useCallback } from "react";
import Styles from "./ChatApplication.module.css";
import {
  Close,
  Done,
  DoneAll,
  Send,
  AttachFile,
  ThumbUp,
  RemoveRedEye,
} from "@mui/icons-material";
import { get_Document_By_id } from "../../Services/ApiCalling/document";
import FileByteView from "../../commons/DocumentView/FileByteView";
import SocketService from "../../commons/Socket/SocketService";
import {
  downloadChat,
  fileUpload,
  getBankingChatByName,
  getConsumerChatByName,
  getGeneratorChatByName,
} from "../../Services/Chat/Chat";
import { ErrorMessage } from "../../commons/Toast/ErrorMessage";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import { formatDateTimeView } from "../../commons/FormatDate";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useLocation } from "react-router-dom";
import { downloadChatHistory } from "./chatPdfGenerator";
import { useSelector } from "react-redux";

// --- Custom Hook for Socket Logic ---

/**
 * Manages the WebSocket connection, message list, and event listeners for a chat.
 * @param {string} chatId - The ID of the chat.
 * @param {string} fromUsername - The username of the current user.
 * @param {string} toUsername - The username of the other user.
 */
const useChatSocket = (chatId, fromUsername, toUsername) => {
  const [messages, setMessages] = useState([]);

  // Define stable event handlers with useCallback
  const handleMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const handleChatHistory = useCallback((history) => {
    setMessages(history);
  }, []);

  const handleMessageReceived = useCallback(({ messageId }) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, read: true } : m)),
    );
  }, []);

  const handleMessageRead = useCallback(({ messageId }) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, read: true } : m)),
    );
  }, []);

  // Effect to manage socket connection and listeners
  useEffect(() => {
    if (!chatId || !fromUsername || !toUsername) return;

    const socket = SocketService.getSocket();

    // 1. Join room
    socket.emit("create-room", { chatId, fromUsername, toUsername });
    socket.emit("join-room", { chatId, username: fromUsername });

    // 2. Register listeners
    socket.on("message", handleMessage);
    socket.on("chat-history", handleChatHistory);
    socket.on("message-received", handleMessageReceived);
    socket.on("message-read", handleMessageRead);

    // 3. Cleanup on unmount or when chat details change
    return () => {
      socket.off("message", handleMessage);
      socket.off("chat-history", handleChatHistory);
      socket.off("message-received", handleMessageReceived);
      socket.off("message-read", handleMessageRead);
    };
  }, [
    chatId,
    fromUsername,
    toUsername,
    handleMessage,
    handleChatHistory,
    handleMessageReceived,
    handleMessageRead,
  ]);

  /**
   * Emits a message payload via the socket.
   */
  const emitSocketMessage = useCallback(
    (payload) => {
      const socket = SocketService.getSocket();
      socket.emit("message", payload);
    },
    [], // SocketService is stable
  );

  return { messages, emitSocketMessage };
};

// --- Presentational Sub-Components ---

const ChatHeader = ({ header, handleClose, handleDownload }) => (
  <div className={`display_flex_row modalHeading ${Styles.headingText}`}>
    <div className={Styles.headingTextChat}>
      <span>Chat : </span>
      <b>{header}</b>
      <button
        onClick={() => {
          handleDownload();
        }}
        style={{ marginLeft: "10px" }}
      >
        <DownloadIcon />
      </button>
    </div>
    <Close
      style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
      onClick={handleClose}
    />
  </div>
);

const MessageBubble = ({ msg, isSelf, onViewFile }) => {
  const downloadFileFromResponse = (response) => {
    // 1. Get the file URL from the response
    const fileUrl = response?.data;

    if (!fileUrl) {
      console.error(
        "Error: The response object does not contain a file URL in the 'data' field.",
      );
      return;
    }

    // 2. Helper to determine the file name from the URL path
    const getFileName = (url) => {
      try {
        // Use URL object to parse the path and remove query parameters
        const path = new URL(url).pathname;

        // Decode URI components to handle spaces and special characters (like %20)
        const decodedPath = decodeURIComponent(path);

        // Extract the filename (the part after the last '/')
        const fileNameWithQuery = decodedPath.substring(
          decodedPath.lastIndexOf("/") + 1,
        );

        // The provided URL contains a filename followed by an S3-specific query parameter.
        // Although the 'download' attribute handles it, cleaning it up here is safer.
        // Since the URL ends with .xlsx followed by query params, we need a good way to extract the filename.
        // For a well-formed S3 URL like this, we'll try to find the extension.

        // A more robust way: find the first occurrence of the file extension
        // before the AWS query params start (usually marked by '?')
        const parts = fileNameWithQuery.split("?");
        return parts[0].trim();
      } catch (e) {
        console.warn("Could not parse file name from URL, using default.");
        return "downloaded_file";
      }
    };

    const fileName = getFileName(fileUrl);

    // 3. Create a temporary anchor element (<a>) to trigger the download
    const link = document.createElement("a");

    // Set the file source URL
    link.href = fileUrl;

    // Set the 'download' attribute to suggest a filename and force download
    link.download = fileName;

    // Append to the document body (required for .click() to work consistently)
    document.body.appendChild(link);

    // 4. Programmatically click the link to start the download
    link.click();

    // 5. Clean up: remove the temporary element
    document.body.removeChild(link);
    console.log(`Download triggered for: ${fileName}`);
  };
  const onDownloadFile = async (fileUrl) => {
    let data = fileUrl?.split("/");
    let id = data?.[data?.length - 1];
    if (!id) throw new Error("Invalid file URL");
    let res = await get_Document_By_id({ type: "chat", id: id });
    downloadFileFromResponse(res);
    // saveAs(ListingTemplateBanking, "Listing Template.xlsx");
  };
  const isPDF = (url) => {
    console.debug("Checking if PDF for url:", url);
    if (!url) return false;
    // A simple check by file extension in the URL
    return url.toLowerCase().endsWith(".pdf");
  };
  return (
    <>
      <div className={isSelf ? Styles.selfMessage : Styles.otherMessage}>
        <div>{msg?.text}</div>
        {msg?.fileUrl && (
          <>
            {isPDF(msg?.text) ? (
              <>
                <RemoveRedEye
                  onClick={() =>
                    onViewFile({ url: msg.fileUrl, name: msg.text })
                  }
                  style={{
                    fontSize: "14px",
                    cursor: "pointer",
                    color: "#f1935c",
                    marginLeft: "8px",
                  }}
                />
              </>
            ) : (
              <>
                <ArrowDownwardIcon
                  onClick={() => onDownloadFile(msg.fileUrl)}
                  style={{
                    fontSize: "14px",
                    cursor: "pointer",
                    color: "#f1935c",
                    marginLeft: "8px",
                  }}
                  title="Download File"
                />
              </>
            )}
          </>
        )}
        {isSelf && (
          <div className={Styles.messageMeta}>
            {msg?.read ? (
              <DoneAll style={{ fontSize: "12px", color: "#34656d" }} />
            ) : (
              <Done style={{ fontSize: "12px" }} />
            )}
          </div>
        )}
      </div>
      <div
        className={isSelf ? Styles.selfMessageTime : Styles.otherMessageTime}
      >
        {formatDateTimeView(msg?.timestamp)}
      </div>
    </>
  );
};

const MessageList = ({
  messages,
  fromUsername,
  onViewFile,
  messagesEndRef,
}) => (
  <div className={`scroll_bar_y ${Styles.viewMessageSection}`}>
    {messages.map((msg, index) => (
      <MessageBubble
        key={msg.id || index}
        msg={msg}
        isSelf={msg.username === fromUsername}
        onViewFile={onViewFile}
      />
    ))}
    <div ref={messagesEndRef} />
  </div>
);

// Assuming Styles and Icons (AttachFile, Send) are imported here

const ChatInput = ({
  messageInput,
  setMessageInput,
  onSendText,
  onFileUpload,
  onSendOk,
}) => {
  // 1. Create a reference to access the textarea DOM element
  const textareaRef = useRef(null);

  // 2. Use useEffect to auto-resize whenever messageInput changes
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to 'auto' first to allow shrinking if text is deleted
      textareaRef.current.style.height = "auto";
      // Set height to the scrollHeight (actual content height)
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [messageInput]);

  // 3. Handle Key Press (Enter = Send, Shift+Enter = New Line)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent adding a new line
      onSendText();
    }
  };

  return (
    <div
      className={Styles.buttonMainContainer}
      style={{ alignItems: "flex-end" }}
    >
      {/* Align items flex-end ensures icons stay at the bottom as box grows */}

      <div style={{ width: "10%", height: "45px" }}>
        <AttachFile
          style={{ fontSize: "16px", color: "#f1935c", cursor: "pointer" }}
          onClick={() => document.getElementById("fileInput").click()}
        />
      </div>

      <div style={{ width: "75%" }} className={Styles.inputMainElem}>
        <input
          type="file"
          id="fileInput"
          onChange={onFileUpload}
          style={{ display: "none" }}
        />

        {/* REPLACED INPUT WITH TEXTAREA */}
        <textarea
          ref={textareaRef}
          rows={1}
          placeholder="Enter your message"
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            width: "100%",
            resize: "none",
            overflowY: "auto",

            // --- UPDATED STYLES FOR CENTERING ---
            minHeight: "45px", // Increased slightly for better touch target
            maxHeight: "45px", // Allow it to grow taller than 30px
            padding: "10px 0px", // 10px top/bottom padding centers the text
            lineHeight: "20px", // Explicit line height ensures stability
            boxSizing: "border-box", // Crucial: Includes padding in height calculation
            // ------------------------------------

            border: "none",
            outline: "none",
            backgroundColor: "transparent",
          }}
        />
      </div>

      <div style={{ width: "10%", height: "45px" }}>
        <Send
          style={{ fontSize: "18px", color: "#525062", cursor: "pointer" }}
          onClick={onSendText}
        />
      </div>
    </div>
  );
};

// --- Main Component ---

export default function ChatApplication({
  open,
  handleClose,
  chatId,
  fromUsername,
  toUsername,
  header,
  hidechat,
}) {
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  );
  const [messageInput, setMessageInput] = useState("");
  const [viewDoc, setViewDoc] = useState("");
  console.log("🚀 ~ ChatApplication ~ viewDoc:", viewDoc);
  const messagesEndRef = useRef(null);

  // Use the custom hook to manage socket state and communication
  const { messages, emitSocketMessage } = useChatSocket(
    chatId,
    fromUsername,
    toUsername,
  );
  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // --- Event Handlers ---

  /**
   * Sends a text message.
   */
  const handleSendText = () => {
    const text = messageInput.trim();

    if (!text || !chatId) return;

    sendMessage(text);
    updateRoomDetails();

    setMessageInput("");
  };

  // Helper function to handle message emission
  const sendMessage = (text) => {
    emitSocketMessage({
      chatId,
      username: fromUsername,
      text,
      fileUrl: null,
      fileType: null,
    });
  };

  // Helper function to handle room updates
  const updateRoomDetails = async () => {
    const socket = SocketService.getSocket();

    let payload = {
      chatId: chatId,
      fromUserId: null,
      toUserId: null,
    };
    if (role === "CONSUMER") {
      let self = await getConsumerChatByName(fromUsername);
      if (self?.data?.code === 200) {
        payload["fromUserId"] = self?.data?.data?.userId;
      }

      let counter = await getGeneratorChatByName(toUsername);
      if (counter?.data?.code === 200) {
        payload["toUserId"] = counter?.data?.data?.userId;
      }
    } else if (role === "GENERATOR") {
      let self = await getGeneratorChatByName(fromUsername);
      if (self?.data?.code === 200) {
        payload["fromUserId"] = self?.data?.data?.userId;
      }

      let counter = await getConsumerChatByName(toUsername);
      if (counter?.data?.code === 200) {
        payload["toUserId"] = counter?.data?.data?.userId;
      }
    } else {
      let self = await getBankingChatByName(fromUsername);
      if (self?.data?.code === 200) {
        payload["fromUserId"] = self?.data?.data?.userId;
      }

      let counter = await getBankingChatByName(toUsername);
      if (counter?.data?.code === 200) {
        payload["toUserId"] = counter?.data?.data?.userId;
      }
    }
    socket.emit("create-room", payload);
  };

  /**
   * Sends a pre-defined "OK" message.
   */
  const handleSendOk = () => {
    emitSocketMessage({
      chatId: chatId,
      username: fromUsername,
      text: "OK",
      fileUrl: null,
      fileType: null,
    });
  };

  /**
   * Handles file selection, validation, upload, and sending.
   */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const acceptedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    // Validation: Check if the file type is NOT included in the acceptedTypes array
    if (!acceptedTypes.includes(file.type)) {
      ErrorMessage("Only PDF and Excel (.xlsx) files are accepted.");
      e.target.value = ""; // Reset the input
      return;
    }

    if (chatId) {
      try {
        const formData = new FormData();
        formData.append("file", file);

        let res = await fileUpload(formData, chatId);

        if (res?.data) {
          emitSocketMessage({
            chatId: chatId,
            username: fromUsername,
            text: file.name,
            fileUrl: res.data,
            fileType: file.type,
          });
          updateRoomDetails();
        }
      } catch (error) {
        ErrorMessage("File upload failed. Please try again.");
        console.error("File upload error:", error);
      }
    }

    e.target.value = ""; // Reset input
  };

  /**
   * Fetches and displays a document.
   */
  const handleViewFile = async ({ url, name }) => {
    try {
      let data = url?.split("/");
      let id = data?.[data?.length - 1];
      if (!id) throw new Error("Invalid file URL");

      let res = await get_Document_By_id({ type: "chat", id: id });
      if (!res?.data) {
        throw new Error("Failed to fetch document");
      }
      setViewDoc({ url: res.data, name: name });
    } catch (error) {
      ErrorMessage(error.message || "Failed to load document.");
      console.error("View file error:", error);
    }
  };

  // async function handleDownload() {
  //   try {
  //     const res = await downloadChat({ chatId, username: toUsername });

  //     const url = URL.createObjectURL(
  //       new Blob([res.data], { type: "application/pdf" })
  //     );

  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = `chat_${fromUsername}_to_${toUsername}.pdf`;
  //     a.click();
  //     URL.revokeObjectURL(url);

  //     toast.success("Chat downloaded successfully!");
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Failed to download chat");
  //   }
  // }

  const handleDownload = async () => {
    let payload = {
      selfListing: null,
      selfCompany: null,
      selfEntity: null,
      counterListing: null,
      counterCompany: null,
      counterEntity: null,
    };
    if (role === "CONSUMER") {
      let self = await getConsumerChatByName(fromUsername);
      if (self?.data?.code === 200) {
        payload["selfListing"] = self?.data?.data?.name;
        payload["selfEntity"] = self?.data?.data?.consumerName;
        payload["selfCompany"] =
          self?.data?.data?.companyName || self?.data?.data?.companyDisplayName;
      }

      let counter = await getGeneratorChatByName(toUsername);
      if (counter?.data?.code === 200) {
        payload["counterListing"] = counter?.data?.data?.name;
        payload["counterEntity"] = counter?.data?.data?.generatorName;
        payload["counterCompany"] =
          counter?.data?.data?.companyName ||
          counter?.data?.data?.companyDisplayName;
      }
    } else if (role === "GENERATOR") {
      let self = await getGeneratorChatByName(fromUsername);
      if (self?.data?.code === 200) {
        payload["selfListing"] = self?.data?.data?.name;
        payload["selfEntity"] = self?.data?.data?.generatorName;
        payload["selfCompany"] =
          self?.data?.data?.companyName || self?.data?.data?.companyDisplayName;
      }

      let counter = await getConsumerChatByName(toUsername);
      if (counter?.data?.code === 200) {
        payload["counterListing"] = counter?.data?.data?.name;
        payload["counterEntity"] = counter?.data?.data?.consumerName;
        payload["counterCompany"] =
          counter?.data?.data?.companyName ||
          counter?.data?.data?.companyDisplayName;
      }
    } else {
      let self = await getBankingChatByName(fromUsername);
      if (self?.data?.code === 200) {
        console.debug(" self?.data?.data", self?.data?.data);
        payload["selfListing"] = self?.data?.data?.name;
        payload["selfEntity"] =
          self?.data?.data?.companyName || self?.data?.data?.companyDisplayName;
        payload["selfCompany"] =
          self?.data?.data?.companyName || self?.data?.data?.companyDisplayName;
      }

      let counter = await getBankingChatByName(toUsername);
      if (counter?.data?.code === 200) {
        payload["counterListing"] = counter?.data?.data?.name;
        payload["counterEntity"] =
          counter?.data?.data?.companyName ||
          counter?.data?.data?.companyDisplayName;
        payload["counterCompany"] =
          counter?.data?.data?.companyName ||
          counter?.data?.data?.companyDisplayName;
      }
    }
    downloadChatHistory(messages, fromUsername, payload);
  };

  return (
    <>
      {viewDoc && (
        <FileByteView open={viewDoc} handleClose={() => setViewDoc(false)} />
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="chat-application-modal"
      >
        <div className={`popupModal ${Styles.chatMainModal}`}>
          <ChatHeader
            header={header}
            handleDownload={handleDownload}
            handleClose={handleClose}
          />

          <MessageList
            messages={messages}
            fromUsername={fromUsername}
            onViewFile={handleViewFile}
            messagesEndRef={messagesEndRef}
          />

          {!window.location.pathname.includes("/archive") && !hidechat && (
            <ChatInput
              messageInput={messageInput}
              setMessageInput={setMessageInput}
              onSendText={handleSendText}
              onFileUpload={handleFileUpload}
              onSendOk={handleSendOk}
            />
          )}
        </div>
      </Modal>
    </>
  );
}
